import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, CreditCard, LayoutGrid, User } from "lucide-react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import Swal from "sweetalert2";

const BottomNav: React.FC = () => {
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const db = getFirestore();

  // Fetch user's status
  useEffect(() => {
    const fetchUserStatus = async () => {
      try {
        const auth = getAuth();
        const userId = auth.currentUser?.uid;

        if (!userId) {
          console.error("No user is logged in");
          setUserStatus(null);
          setLoading(false);
          return;
        }

        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserStatus(userData?.status);
        } else {
          console.error("User document not found");
          setUserStatus(null);
        }
      } catch (error) {
        console.error("Error fetching user status:", error);
        setUserStatus(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserStatus();
  }, [db]);

  // Handle restricted navigation
  const handleNavigation = (route: string) => {
    if (loading) {
      Swal.fire({
        icon: "info",
        title: "Loading...",
        text: "Please wait while we check your account status.",
        showConfirmButton: false,
        timer: 1500,
      });
      return;
    }

    if (route === "/quizscreen") {
      if (userStatus === "verified") {
        navigate(route); // Allow navigation if verified
      } else if (userStatus === "unverified") {
        Swal.fire({
          icon: "warning",
          title: "Account Not Verified",
          text: "Please verify your account to access this feature.",
          confirmButtonText: "Okay",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Unable to determine your account status. Please try again later.",
          confirmButtonText: "Okay",
        });
      }
    } else {
      navigate(route); // Allow navigation for all other routes
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-900 p-3 shadow-lg sm:hidden z-50">
      <div className="max-w-md mx-auto flex justify-between">
        {[
          { icon: <Home size={20} />, label: "Dashboard", route: "/dashboard" },
          { icon: <CreditCard size={20} />, label: "Transactions", route: "/history" },
          { icon: <LayoutGrid size={20} />, label: "Games", route: "/quizscreen" },
          { icon: <User size={20} />, label: "Profile", route: "/UserProfile" },
        ].map((item, index) => (
          <div
            key={index}
            onClick={() => handleNavigation(item.route)}
            className={`flex flex-col items-center gap-1 cursor-pointer ${
              index === 0 ? "text-white" : "text-gray-500 hover:text-white transition-colors"
            }`}
          >
            {item.icon}
            <span className="text-xs">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BottomNav;
